import { put, select, all, call } from 'redux-saga/effects';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import isNaN from 'lodash/isNaN';
import { PayloadAction } from '@reduxjs/toolkit';
import { setIn } from 'utilitify';
import format from 'date-fns/format';

import {
  xcriticalFormPropertyChange,
  xcriticalFormError,
  xcriticalFormShowErrors,
  IFormAction,
  formSelector,
  xcriticalFormSetFieldsMeta,
  xcriticalFormInit,
} from '@xcritical/forms';
import { notify } from '@xcritical/notification';

import { crmGridSetItems, gridSelector } from '@crm-framework/grid';
import { registryActions } from '@crm-framework/registry';
import { handleServerError } from '@crm-framework/utils';
import { getUser } from '@crm-framework/auth';
import { GRT } from '@crm-framework/types';

import {
  dictionariesActions,
  getDictionariesByName,
  IBusinessUnitModel,
  IEnvironmentModel,
  ILanguageModel,
  loadDictionaryMethods,
  mapBUbyEnvironments,
  loadTransactionSubTypesDictionaryByType,
} from '@crm-package/dictionaries';
import { formDatePickerFormat, getSpecialOffers } from '@crm-package/app';

import { validate } from '../validate';
import {
  IRemovePromoRule,
  IGetPromoPayload,
  ISavePromo,
  promoActions,
} from '../actions';
import {
  IPromoModel,
  IPromoType,
  IBonusConditions,
  DictionaryPromise,
  locations,
  IText,
  ISetDictionaries,
  IDictionaryItem,
  IPromoDictionaries,
  DictionariesForSetType,
} from '../types';
import * as consts from '../consts';
import {
  getErrorsArray,
  getDefaultPromoFormData,
  disabledAll,
  getBUByRights,
  isNewPromo,
  stringToDictionaryItem,
  isLeadPromo,
} from '../utils';
import { TRANSACTION_SUB_TYPE_CREDIT_DICTIONARY_NAME } from '../consts';

import {
  apiGetPromo,
  apiGetMyAccountURLs,
  apiUpdatePromo,
  apiCreatePromo,
  apiGetLeadPromo,
} from './api';

const validateActionPayload = {
  meta: {
    formName: consts.PROMO_FORM_NAME,
  },
  payload: {
    value: '',
  },
} as IFormAction;

function* setCurrenciesDictionaries(promoData: IPromoModel) {
  const { depositCurrencies, devaluationCurrencies } = yield select(
    getSpecialOffers
  );
  const {
    rule: {
      depositCurrency: formDepositCurrency,
      devaluationCurrency: formDevaluationCurrency,
    },
  }: IPromoModel = promoData;

  const mappedDepositCurrencies = depositCurrencies.map(stringToDictionaryItem);
  const mappedDevaluationCurrencies = devaluationCurrencies.map(
    stringToDictionaryItem
  );

  if (formDepositCurrency && !depositCurrencies.includes(formDepositCurrency)) {
    mappedDepositCurrencies.push({
      id: formDepositCurrency,
      name: formDepositCurrency,
      disabled: true,
    });
  }

  if (
    formDevaluationCurrency &&
    !devaluationCurrencies.includes(formDevaluationCurrency)
  ) {
    mappedDevaluationCurrencies.push({
      id: formDevaluationCurrency,
      name: formDevaluationCurrency,
      disabled: true,
    });
  }

  yield put(
    dictionariesActions.addOrUpdate({
      depositCurrencies: mappedDepositCurrencies,
      devaluationCurrencies: mappedDevaluationCurrencies,
    })
  );
}

function* setDictionaries({ location }: ISetDictionaries) {
  const dictionariesRequests: { [key: string]: DictionaryPromise } = {};
  const dictionaries: DictionariesForSetType = {};

  const {
    environment,
    language,
    businessUnit,
    visible,
    leadRetentionFilter,
    myAccountUrls,
    mappedTypes,
    type: types,
  } = yield* getDictionariesByName<IPromoDictionaries>([
    'environment',
    'language',
    'businessUnit',
    'visible',
    'leadRetentionFilter',
    'myAccountUrls',
    'mappedTypes',
    'type',
  ]);

  if (isEmpty(visible)) {
    dictionaries.visible = consts.visible;
  }

  if (isEmpty(leadRetentionFilter)) {
    dictionaries.leadRetentionFilter = consts.leadRetentionFilter;
  }

  if (isEmpty(myAccountUrls)) {
    dictionariesRequests.myAccountUrls = apiGetMyAccountURLs();
  }

  if (isEmpty(language)) {
    dictionariesRequests.language = yield loadDictionaryMethods.language();
  }

  if (isEmpty(environment)) {
    dictionariesRequests.environment =
      yield loadDictionaryMethods.environment();
  }

  const dictionariesFromServer: {
    [key: string]: IDictionaryItem[];
  } = yield all(dictionariesRequests);

  if (isEmpty(businessUnit)) {
    let $businessUnits: IBusinessUnitModel[] =
      yield loadDictionaryMethods.businessUnit();

    const { rights, businessUnitRights } = yield select(getUser);

    if (location === locations.LOCATION_PROMO_DASHBOARD) {
      $businessUnits = getBUByRights({
        userBURight: 'promo View',
        rights,
        businessUnitRights,
        businessUnits: $businessUnits,
      });

      const editableBUIds = getBUByRights({
        userBURight: 'promo Edit',
        rights,
        businessUnitRights,
        businessUnits: $businessUnits,
      }).map((BU) => BU.id);

      $businessUnits = $businessUnits.map((BU) => {
        if (editableBUIds.includes(BU.id)) return BU;

        return {
          ...BU,
          disabled: true,
        };
      });
    } else if (location === locations.LOCATION_LEAD_DASHBOARD) {
      $businessUnits = getBUByRights({
        userBURight: 'accounts Promo View',
        rights,
        businessUnitRights,
        businessUnits: $businessUnits,
      }).map(disabledAll);
    }

    const environments =
      dictionariesFromServer.environment as IEnvironmentModel[];
    const mappedBusinessUnits = mapBUbyEnvironments(
      environments,
      $businessUnits
    );
    dictionaries.businessUnit = $businessUnits;
    dictionaries.businessUnitsByEnviroments = mappedBusinessUnits;
  }

  if (isEmpty(mappedTypes)) {
    dictionaries.mappedTypes = types.map(({ alias, name }: IPromoType) => ({
      id: alias,
      name,
    }));
  }

  if (dictionariesFromServer.language) {
    const eng = dictionariesFromServer.language.find(
      ({ name }) => name === 'English'
    )!;

    const filteredLanguages = dictionariesFromServer.language.filter(
      ({ name }) => !['Unknown', 'English'].includes(name)
    );

    dictionariesFromServer.language = [eng, ...filteredLanguages];
  }

  yield* loadTransactionSubTypesDictionaryByType([
    TRANSACTION_SUB_TYPE_CREDIT_DICTIONARY_NAME,
  ]);

  if (!isEmpty(dictionaries) || !isEmpty(dictionariesFromServer)) {
    yield put(
      dictionariesActions.addOrUpdate({
        ...dictionaries,
        ...dictionariesFromServer,
      })
    );
  }
}

/* change order, set name */
function formatLanguages(promoData: IPromoModel, languages: ILanguageModel[]) {
  const langsWithNewOrder = languages.map((lang, i) => ({ ...lang, order: i }));

  const mappedTexts = promoData.texts
    .map((text) => {
      const { name, order } = langsWithNewOrder.find(
        ({ id }) => id === text.languageId
      )!;

      return {
        ...text,
        order,
        languageName: name,
      };
    })
    .sort((a, b) => a.order - b.order);

  return setIn(promoData, mappedTexts, ['texts']);
}

export function* handleGetPromo({
  payload: { id, location, leadId },
}: PayloadAction<IGetPromoPayload>) {
  try {
    yield put(registryActions.set(consts.PROMO_REGISTRIES.LOADING, true));
    yield put(registryActions.remove(consts.PROMO_REGISTRIES.ACTIVE_LANG));
    yield put(registryActions.remove(consts.PROMO_REGISTRIES.ACTIVE_TAB));

    yield call(setDictionaries, { location });

    let promoData: GRT<typeof apiGetPromo> | null = null;

    if (!isNewPromo(id)) {
      promoData = isLeadPromo(location)
        ? yield apiGetLeadPromo(id, leadId!)
        : yield apiGetPromo(id);

      promoData!.validFrom = new Date(promoData!.validFrom);
      promoData!.validTo = new Date(promoData!.validTo);
      Object.entries(promoData!).forEach(([key, value]) => {
        if (consts.limitTypesKeys.includes(key))
          promoData![key] = String(value);
      });
    } else {
      promoData = yield select(getDefaultPromoFormData);
    }

    const { language } = yield* getDictionariesByName(['language']);
    promoData = formatLanguages(promoData!, language);

    yield call(setCurrenciesDictionaries, promoData);

    yield put(xcriticalFormInit(consts.PROMO_FORM_NAME, promoData));

    if (!isNewPromo(id) && !isLeadPromo(location)) {
      yield call(validateForm, validateActionPayload);
      yield put(xcriticalFormShowErrors(consts.PROMO_FORM_NAME, true));
    }
  } catch (e) {
    console.error(e);
    handleServerError(e);
  } finally {
    yield put(registryActions.set(consts.PROMO_REGISTRIES.LOADING, false));
  }
}

function* touchRules(length: number) {
  const { fields = {} } = yield select(formSelector, consts.PROMO_FORM_NAME);

  const touched = { touch: true, changed: true };

  const touchedRule = {
    value: touched,
    amountTo: touched,
    amountFrom: touched,
  };

  const newRulesMeta = new Array(length).fill(touchedRule);

  const newMeta = setIn(fields, newRulesMeta, ['rule', 'bonusConditions']);

  yield put(xcriticalFormSetFieldsMeta(consts.PROMO_FORM_NAME, newMeta));
}

export function* handleAddRule() {
  const form = yield select(formSelector, consts.PROMO_FORM_NAME);
  const rules = get(form, ['model', 'rule', 'bonusConditions'], []);

  const lastRuleValueAmountTo = Number(
    get(rules, [rules.length - 1, 'amountTo'], null)
  );
  let newRuleAmountFrom: string | undefined;

  if (!isNaN(lastRuleValueAmountTo)) {
    const amountFrom = lastRuleValueAmountTo + 0.01;
    newRuleAmountFrom = amountFrom > 0 ? amountFrom.toFixed(2) : '';
  }

  const newRules = [
    ...rules,
    { amountFrom: newRuleAmountFrom, amountTo: null, value: null },
  ];

  yield put(
    xcriticalFormPropertyChange(
      consts.PROMO_FORM_NAME,
      'rule.bonusConditions',
      newRules
    )
  );

  yield* touchRules(newRules.length);
}

export function* handleRemoveRule({
  payload: { index },
}: PayloadAction<IRemovePromoRule>) {
  const form = yield select(formSelector, consts.PROMO_FORM_NAME);
  const rules: IBonusConditions[] = get(
    form,
    ['model', 'rule', 'bonusConditions'],
    []
  );
  const newRules = rules.filter(
    (_: IBonusConditions, i: number) => i !== index
  );

  yield put(
    xcriticalFormPropertyChange(
      consts.PROMO_FORM_NAME,
      'rule.bonusConditions',
      newRules
    )
  );

  yield* touchRules(newRules.length);
}

export function* validateForm({
  meta: { formName },
  payload: { value },
}: IFormAction) {
  if (formName !== consts.PROMO_FORM_NAME) return;

  if (typeof value !== 'string') return;

  const state = yield select(($state) => $state);
  const { model } = formSelector<IPromoModel>(state, consts.PROMO_FORM_NAME);

  const errors = validate(model, state);
  yield put(xcriticalFormError(consts.PROMO_FORM_NAME, errors));
}

export function* handleSavePromo({
  payload: { id },
}: PayloadAction<ISavePromo>) {
  try {
    yield put(xcriticalFormShowErrors(consts.PROMO_FORM_NAME, true));
    yield call(validateForm, validateActionPayload);

    const promoForm = yield select(formSelector, consts.PROMO_FORM_NAME);
    const model = { ...get(promoForm, ['model']) };
    const errors = get(promoForm, ['errors'], []);
    const errorsArray = getErrorsArray(errors);

    if (errorsArray.length !== 0) {
      const textWithError = get(errors, ['texts'], [])
        .map((text: IText, index: number) => ({ ...text, index }))
        .find(({ description, emailBody }: IText) => description || emailBody);

      if (textWithError) {
        if (textWithError.description) {
          yield put(registryActions.set(consts.PROMO_REGISTRIES.ACTIVE_TAB, 0));
        } else if (textWithError.emailBody) {
          yield put(registryActions.set(consts.PROMO_REGISTRIES.ACTIVE_TAB, 1));
        }

        yield put(
          registryActions.set(
            consts.PROMO_REGISTRIES.ACTIVE_LANG,
            textWithError.index
          )
        );
      }

      return;
    }

    model.validFrom = format(model.validFrom, formDatePickerFormat);
    model.validTo = format(model.validTo, formDatePickerFormat);
    delete model.environmentForLink;

    if (!isNewPromo(id)) {
      const newModel: GRT<typeof apiUpdatePromo> = yield apiUpdatePromo(
        id,
        model
      );
      notify.success('Promo successfully updated!');
      yield call(updateGrid, newModel, id);
    } else {
      const newModel: GRT<typeof apiCreatePromo> = yield apiCreatePromo(model);
      yield call(updateGrid, newModel);
      notify.success('Promo successfully created!');
    }

    yield put(
      promoActions.getData({ id, location: locations.LOCATION_PROMO_DASHBOARD })
    );
  } catch (error) {
    let message = 'Server error. Please, try again later.';

    if (error.message && JSON.parse(error.message)) {
      const parsed = JSON.parse(error.message);
      message = parsed.Message || parsed.message;
    }

    notify.error(message);
    console.error(error.json);
  }
}

function* updateGrid(model: IPromoModel, id?: string) {
  const { items } = yield select(gridSelector, consts.PROMO_GRID_NAME);

  if (isEmpty(items)) return;

  if (id) {
    const index = items.findIndex(
      (item: IPromoModel) => String(item.id) === id
    );
    const newItems = setIn(items, model, index);
    yield put(crmGridSetItems(consts.PROMO_GRID_NAME, newItems));
  } else {
    const newItems = [model, ...items];
    yield put(crmGridSetItems(consts.PROMO_GRID_NAME, newItems));
  }
}
